import {createAsyncThunk, createSelector, createSlice} from '@reduxjs/toolkit';
import {AsyncState, RejectedState} from '../../models/async_state';
import {Status} from '../../enums/status';
import {GetNotificationsQuery} from '../../gql/customer/graphql';
import {Customer} from '../../network/customer';
import {RootState} from '../store';
import {getToken} from 'firebase/messaging';
import {messaging, vapidKey} from '../../firebase/config.ts';

interface State extends GetNotificationsQuery {
    deviceToken: string | null;
    deviceTokenRegistered: boolean;
    permissionState: 'default' | 'denied' | 'granted';
}

export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async (data: {token: string}) => {
        const result = await Customer.notifications(data.token);
        return result.data;
    },
);

export const markNotificationRead = createAsyncThunk(
    'notifications/markNotificationRead',
    async (data: {token: string; notificationId: number}) => {
        const result = await Customer.markNotificationRead(data.token, data.notificationId);
        return {
            notificationId: data.notificationId,
            readNotification: result.data?.readNotification,
        };
    },
);

export const registerDeviceToken = createAsyncThunk(
    'notifications/registerDeviceToken',
    async (data: {token: string}) => {
        const permission = await Notification.requestPermission();
        if (permission === 'granted' && messaging) {
            const deviceToken = await getToken(messaging, {
                vapidKey: vapidKey,
            });

            const registeredDevicesResult = await Customer.registeredDevices(data.token);

            const customerDevices = registeredDevicesResult.data.me?.customerDevices;
            if (!customerDevices?.find((device) => device?.firebase_token === deviceToken)) {
                await Customer.registerCustomerDevice(data.token, deviceToken, 'web');
            }

            return {permissionState: permission, deviceToken: deviceToken};
        } else {
            return {permissionState: permission, deviceToken: ''};
        }
    },
);

export const notificationSelector = createSelector(
    (state: RootState) => state.notifications.notifications,
    (_: RootState, id: number | null) => id,
    (notifications, id) => notifications?.find((n) => id != null && n?.id === id) ?? undefined,
);

export const unreadNotificationsCountSelector = (state: RootState) => {
    return unreadNotificationsSelector(state).length;
};

export const unreadNotificationsSelector = createSelector(
    (state: RootState) => state.notifications.notifications,
    (notifications) => {
        return (notifications ?? []).filter((n) => n?.read === false);
    },
);

export const notificationsSlice = createSlice({
    name: 'notifications',
    initialState: () => {
        const existingDeviceToken = localStorage.getItem('device_token');
        const iosMobileDevice = /(iPhone|iPod|iPad)/i.test(navigator.userAgent);

        let permissionState: NotificationPermission = 'default';
        if (!iosMobileDevice) {
            permissionState = Notification.permission;
        }
        const initialState: AsyncState<State> = {
            status: Status.IDLE,
            deviceToken: existingDeviceToken,
            deviceTokenRegistered: existingDeviceToken !== null,
            permissionState: permissionState,
        };
        return initialState as AsyncState<State>;
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchNotifications.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(fetchNotifications.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            state.notifications = action.payload.notifications;
            state.__typename = action.payload.__typename;
        });
        builder.addCase(fetchNotifications.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(markNotificationRead.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(markNotificationRead.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (state.notifications) {
                state.notifications = state.notifications?.map((notification) => {
                    if (notification && notification.id === action.payload.notificationId) {
                        return {
                            ...notification,
                            read: true,
                            __typename: 'NotificationType',
                        };
                    }
                    return notification;
                });
            }
        });
        builder.addCase(markNotificationRead.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
        builder.addCase(registerDeviceToken.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(registerDeviceToken.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (action.payload) {
                localStorage.setItem('device_token', action.payload.deviceToken);
                state.deviceToken = action.payload.deviceToken;
                state.permissionState = action.payload.permissionState;
                state.deviceTokenRegistered = action.payload.deviceToken !== '';
            }
        });
        builder.addCase(registerDeviceToken.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
    },
});
